<script>
  import { onMount } from 'svelte';
  import Link from './Link.svelte';

  export let header = 'Заголовок слайдера';
  export let component;
  export let data = [];
  export let type = 'gray';
  export let media;
  export let attributes = {};
  export let buttons = true;
  export let url = '';

  $: maxMedia = media[0];
  $: count = maxMedia.count;
  $: gap = maxMedia.gap;
  $: maxOffset = Math.ceil(data.length / count);
  $: offset = 0;
  let slider;

  const getSize = () => slider.children[0].getBoundingClientRect().width + gap;
  const move = offset => (slider.scrollLeft = offset * count * getSize());
  const onResize = () => {
    const { documentElement, body } = document;
    const size = window.innerWidth || documentElement.clientWidth || body.clientWidth;

    const data = media.reduce((acc, media) => {
      if (size > media.size) return acc;
      return media;
    });
    if (!data) return void move(offset);
    offset = 0;
    if (maxMedia.count >= data.count) count = data.count;
    if (maxMedia.gap >= data.gap) gap = data.gap;
    move(offset);
  };

  onMount(onResize);

  let background, className;
  $: {
    [background, className] = type.startsWith('#') ? [`background: ${type};`, ''] : ['', type];
    if (className === '') break $;
    className = className === 'gray' ? 'logo-pattern' : className;
    className = className === 'white' ? '' : className;
    className = className === 'purple' ? 'purple-mode' : className;
  }
</script>

<svelte:window on:resize={onResize} />

<section
  {...attributes}
  style={data.length ? background : 'display:none;'}
  class="slider-container container {className}">
  <div>
    <h2 class="h2">{header}</h2>
    <div>
      <button
        class="push-button"
        aria-label="пролистать слайдер влево"
        type="button"
        disabled={offset === 0}
        on:click={() => move((offset -= 1))} />
      <button
        class="push-button"
        aria-label="пролистать слайдер вправо"
        type="button"
        disabled={offset === maxOffset - 1}
        on:click={() => move((offset += 1))} />
    </div>
  </div>
  <ul class="slider" bind:this={slider} style={`--columns: ${count}; --gap: ${gap}px;`}>
    {#each data as item}
      <svelte:component this={component} data={item} />
    {/each}
  </ul>
  {#if buttons && maxOffset > 1}
    <div class="slider-steps">
      {#each Array(maxOffset)
        .fill(0)
        .map((_, i) => i) as button}
        <button
          on:click={() => move((offset = button))}
          class={offset === button ? 'active push-button' : 'push-button'}
          aria-label="Переключить слайд"
          type="button">{button + 1}</button>
      {/each}
    </div>
  {/if}
  {#if url}
    <Link href={url}>Показать ещё</Link>
  {/if}
</section>

<style lang="scss">
  .slider-steps {
    display: inline-flex;
    gap: 8px;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding-top: 32px;

    @media only screen and (max-width: 500px) {
      display: none;
    }

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      height: 18px;
      font-weight: 800;
      font-size: 12px;
      font-family: Roboto;
      background: #2c333c26;
      border-radius: 50%;
    }

    :global(.active) {
      color: #fff;
      background: #22eaac !important;
    }
  }

  section {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    padding-block: 50px;

    :global(> a) {
      gap: 10px;
      margin-top: 50px;
      margin-left: 50%;
      padding: 18px 40px;
      color: var(--primary-dark, #2c333c);
      font-weight: 600;
      font-size: 16px;
      font-family: Vela Sans;
      font-style: normal;
      line-height: 140%; /* 22.4px */
      white-space: nowrap;
      text-align: center;
      border: 2px solid rgb(44 51 60 / 75%);
      border-radius: 5px;
      transform: translateX(-50%);
      transition: all 0.1s;

      &:hover {
        background-color: #22eaac;
        border-color: #22eaac;
      }
    }

    > .slider {
      -ms-overflow-style: none; /* IE and Edge */
      scrollbar-width: none; /* Firefox */
      position: relative;
      scroll-behavior: smooth;
      display: grid;
      grid-auto-columns: minmax(calc((100% / var(--columns) - var(--gap) + var(--gap) / var(--columns))), 1fr);
      grid-auto-flow: column;
      grid-gap: var(--gap);
      grid-template-columns: repeat(
        auto-fill,
        minmax(calc((100% / var(--columns) - var(--gap) + var(--gap) / var(--columns))), 1fr)
      );
      width: 100%;
      padding-top: 56px;
      overflow: hidden;

      &::-webkit-scrollbar {
        height: 10px;
        background-color: #f6f7f8;
      }

      &::-webkit-scrollbar-thumb {
        background-color: #22eaac;
        border-radius: 8px;
      }

      @media only screen and (max-width: 500px) {
        overflow-x: auto;
      }
    }

    > div:first-child {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;

      h2 {
        color: #2c333c;
        font-weight: 800;
        font-family: Vela Sans;
      }

      div {
        display: flex;
        gap: 25px;

        @media only screen and (max-width: 500px) {
          display: none;
        }

        button {
          width: 40px;
          height: 40px;

          &:disabled {
            opacity: 0.3;
          }

          &::after {
            display: block;
            scale: 0.9;
            width: 30px;
            height: 30px;
            background-color: #2c333c;
            content: '';
            clip-path: polygon(50% 75%, 100% 25%, 91% 17%, 50% 57%, 9% 16%, 0 25%);
          }
        }

        > button:first-child {
          &::after {
            transform: rotate(90deg);
          }
        }

        > button:last-child {
          &::after {
            transform: rotate(-90deg);
          }
        }
      }
    }
  }
</style>
